import type { FC } from 'react';
import { useCallback, useContext, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
// @dynamic-i18n-keys
// Dynamic t() at lines 170, 233 (studio.products.actions.create.${type}, studio.products.categories.${type}; type from ProductType):
//   studio.products.actions.create.tier
//   studio.products.actions.create.premium_gallery
//   studio.products.actions.create.commission
//   studio.products.actions.create.originals
//   studio.products.actions.create.deviation
//   studio.products.categories.exclusive
//   studio.products.categories.exclusive_resale
//   studio.products.categories.pcp
//   studio.products.categories.premium_gallery
//   studio.products.categories.commission
//   studio.products.categories.originals
//   studio.products.categories.tier
import cn from 'classnames';
import { Url } from '@wix/da-url';
import { useAction } from '@wix/da-hooks/pkg/useAction';
import { MobileContext } from '@wix/da-react-context/pkg/MobileContext';
import { BiData, SeeAllClickBiEvent } from '@wix/da-bi/pkg/events';
import SaleIcon from '@wix/da-ds/pkg/Icons/24x24/Sale';
import PremiumDownloadIcon from '@wix/da-ds/pkg/Icons/24x24/PremiumDownload';
import PremiumGalleryIcon from '@wix/da-ds/pkg/Icons/24x24/PremiumGallery';
import CommissionIcon from '@wix/da-ds/pkg/Icons/24x24/CommissionBrush';
import OriginalIcon from '@wix/da-ds/pkg/Icons/24x24/OriginalBrush';
import TierIcon from '@wix/da-ds/pkg/Icons/24x24/Tier';
import ChevronRightIcon from '@wix/da-ds/pkg/Icons/24x24/ChevronRight';
import { IconSize } from '@wix/da-ds/pkg/Icons/IconWrap';
import SolidButton from '@wix/da-ds/pkg/Buttons/SolidButton';
import GhostButton from '@wix/da-ds/pkg/Buttons/GhostButton';
import IconButton from '@wix/da-ds/pkg/Buttons/IconButton';
import TextButton from '@wix/da-ds/pkg/Buttons/TextButton';
import PlainButtonOrLink from '@wix/da-ds/pkg/Buttons/PlainButtonOrLink';
import { pushModal } from '@wix/da-shared-react/pkg/Modals/redux/basicActions';
import { ModalType } from '@wix/da-shared-react/pkg/Modals/redux/types';
import { ProductType } from '../types';

import s from './ProductCategory.scss';

interface Props {
  type: ProductType;
  productCount: number;
  originalsNewOrders?: number;
}

const ProductCategory: FC<Props> = ({ type, productCount, originalsNewOrders }) => {
  const { t } = useTranslation();
  const isMobile = useContext(MobileContext);
  const hasItems = productCount > 0;
  let icon;

  const dispatchPushModal = useAction(pushModal);
  const onCreateTierClick = useCallback(
    () =>
      dispatchPushModal(ModalType.TIER_EDIT, {
        wrapInModal: false,
      }),
    [dispatchPushModal]
  );

  const onCreateCommissionClick = useCallback(
    () =>
      dispatchPushModal(ModalType.COMMISSION, {
        wrapInModal: false,
      }),
    [dispatchPushModal]
  );

  const onCreateOriginalClick = useCallback(
    () =>
      dispatchPushModal(ModalType.ORIGINAL_DEVIATION, {
        wrapInModal: false,
      }),
    [dispatchPushModal]
  );

  const actionProps = useMemo(() => {
    if (hasItems) {
      const href = Url.studio({
        products: type as Parameters<typeof Url.studio>[0]['products'],
      });
      return {
        href,
        biData: BiData<SeeAllClickBiEvent>({
          evid: 106,
          sectiontype: type,
          sectionname: 'manage',
        }),
      };
    }
    let href;
    let onClick;

    switch (type) {
      case ProductType.Exclusive:
      case ProductType.PremiumDownload:
        href = Url.studio({ openSubmitForm: true });
        break;
      case ProductType.PremiumGallery:
        href = Url.studio({ published: 'galleries', openSubmitForm: true });
        break;
      case ProductType.Commission:
        onClick = onCreateCommissionClick;
        break;
      case ProductType.Original:
        onClick = onCreateOriginalClick;
        break;
      case ProductType.Tier:
        onClick = onCreateTierClick;
        break;
    }
    return { href, onClick };
  }, [hasItems, type, onCreateCommissionClick, onCreateOriginalClick, onCreateTierClick]);

  const actionButton = useMemo(() => {
    if (isMobile) {
      if (hasItems) {
        return (
          <IconButton
            size="medium"
            variant="main"
            icon={ChevronRightIcon}
            className={s['action-button']}
            aria-label={t('studio.products.actions.manage')}
            {...actionProps}
          />
        );
      }
      return (
        <TextButton size="medium" variant="main" className={s['action-button']} {...actionProps}>
          {t('studio.products.lobby.create')}
        </TextButton>
      );
    }

    // Default button styles
    if (hasItems) {
      return (
        <SolidButton size="medium" variant="main" className={s['action-button']} {...actionProps}>
          {t('studio.products.actions.manage')}
        </SolidButton>
      );
    }
    const title = [
      ProductType.Tier,
      ProductType.PremiumGallery,
      ProductType.Commission,
      ProductType.Original,
    ].includes(type)
      ? t(`studio.products.actions.create.${type}`)
      : t('studio.products.actions.create.deviation');

    return (
      <GhostButton size="medium" variant="main" className={s['action-button']} {...actionProps}>
        {title}
      </GhostButton>
    );
  }, [t, hasItems, type, actionProps, isMobile]);

  switch (type) {
    case ProductType.Exclusive:
      icon = <SaleIcon size={IconSize.MEDIUM} disableDefaultColors />;
      break;
    case ProductType.PremiumDownload:
      icon = <PremiumDownloadIcon size={IconSize.MEDIUM} disableDefaultColors />;
      break;
    case ProductType.PremiumGallery:
      icon = <PremiumGalleryIcon size={IconSize.MEDIUM} disableDefaultColors />;
      break;
    case ProductType.Commission:
      icon = <CommissionIcon size={IconSize.MEDIUM} disableDefaultColors />;
      break;
    case ProductType.Original:
      icon = <OriginalIcon size={IconSize.MEDIUM} disableDefaultColors />;
      break;
    case ProductType.Tier:
      icon = <TierIcon size={IconSize.MEDIUM} disableDefaultColors />;
      break;
  }

  const thumbnailProps = useMemo(() => {
    if (hasItems) {
      const href = Url.studio({
        products: type as Parameters<typeof Url.studio>[0]['products'],
      });
      return {
        href,
        biData: BiData<SeeAllClickBiEvent>({
          evid: 106,
          sectiontype: type,
          sectionname: 'thumb',
        }),
      };
    }
    return actionProps;
  }, [hasItems, type, actionProps]);

  const isOriginals = type === ProductType.Original;
  const showNewOrders = isOriginals && originalsNewOrders !== undefined && originalsNewOrders > 0;

  const titleAndCount = (
    <>
      <div className={s['title-wrapper']}>
        <PlainButtonOrLink className={s['title']} {...thumbnailProps}>
          {t(`studio.products.categories.${type}`)}
        </PlainButtonOrLink>
        {showNewOrders && (
          <span className={s['new-orders']}>
            {t('studio.products.originals.newOrders', {
              count: originalsNewOrders,
            })}
          </span>
        )}
      </div>
      <div className={s['product-count']}>
        {isMobile
          ? productCount
          : t('studio.products.productCount', {
              postProcess: 'reactPostprocessor',
              count: productCount,
              countDisplay: <span>{productCount}</span>,
            })}
      </div>
    </>
  );

  return (
    <div className={cn(s['root'], productCount === 0 && s['empty'])}>
      <PlainButtonOrLink className={s['icon']} {...thumbnailProps}>
        <div className={s[`icon-${type}`]}>{icon}</div>
      </PlainButtonOrLink>
      {isMobile ? <div className={s['title-and-count']}>{titleAndCount}</div> : titleAndCount}
      <div className={s['actions']}>{actionButton}</div>
    </div>
  );
};
ProductCategory.displayName = 'ProductCategory';

export default ProductCategory;
